import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { FileText, BarChart3, TrendingUp, Download, Clock, CheckCircle, Shield, Lock } from 'lucide-react';

const reportTypes = [
  {
    icon: BarChart3,
    title: 'Weekly Performance',
    description: 'ACOS, TACOS, sales, and spend trends with week-over-week analysis.',
    format: 'PDF + Dashboard',
    frequency: 'Every Monday',
  },
  {
    icon: TrendingUp,
    title: 'Monthly Strategy Review',
    description: 'Deep dive into performance, opportunities, and strategic recommendations.',
    format: 'PDF + Presentation',
    frequency: 'Monthly',
  },
  {
    icon: FileText,
    title: 'Search Term Analysis',
    description: 'Keyword performance, new opportunities, and negative keyword suggestions.',
    format: 'Spreadsheet + Summary',
    frequency: 'Bi-weekly',
  },
];

const credibilityIndicators = [
  { icon: Shield, label: 'SOC 2 Compliant', desc: 'Enterprise security standards' },
  { icon: Lock, label: 'Data Encryption', desc: 'AES-256 at rest and in transit' },
];

export default function EnhancedReportingSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      id="reporting"
      ref={sectionRef}
      className="relative bg-[#05060B] py-20 lg:py-24 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,182,212,0.08)_0%,transparent_60%)] pointer-events-none" />

      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20">
        <div className="max-w-7xl mx-auto">
          <div 
            className={`text-center mb-12 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-6">
              Reporting
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Reports that{' '}
              <span className="text-cyan-400 cyan-glow">tell the story.</span>
            </h2>
            <p className="text-lg text-dark-400 max-w-2xl mx-auto">
              Clear, actionable reports delivered on your schedule. No fluff—just insights you can act on.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {reportTypes.map((report, index) => (
              <div
                key={index}
                className={`glass-card p-6 group hover-lift ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100 + 200}ms` }}
              >
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-colors">
                  <report.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2 group-hover:text-cyan-400 transition-colors">
                  {report.title}
                </h3>
                <p className="text-dark-400 text-sm leading-relaxed mb-4">
                  {report.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-cyan-500/10">
                  <div className="flex items-center gap-2 text-xs text-dark-500">
                    <Download className="w-3 h-3" />
                    {report.format}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-cyan-400">
                    <Clock className="w-3 h-3" />
                    {report.frequency}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div 
            className={`glass-card p-6 mb-12 transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h3 className="font-heading font-semibold text-xl text-white mb-4">
                  Sample Weekly Report
                </h3>
                <p className="text-dark-400 mb-6 text-sm">
                  Every report includes executive summary, key metrics, AI-generated insights, and specific action items.
                </p>
                <ul className="space-y-2">
                  {[
                    'Executive summary with key takeaways',
                    'Performance metrics with trend analysis',
                    'AI-generated opportunities and alerts',
                    'Specific action items with priorities',
                    'Budget pacing and forecast',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-dark-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="bg-dark-900/50 rounded-xl border border-cyan-500/10 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-white">Weekly Performance Summary</span>
                    <span className="text-xs text-dark-500">Week of Jan 15-21</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-dark-800/50">
                      <div className="text-xs text-dark-500 mb-1">Ad Sales</div>
                      <div className="text-base font-bold text-white">$12.4K</div>
                      <div className="text-xs text-green-400">+8.2%</div>
                    </div>
                    <div className="p-3 rounded-lg bg-dark-800/50">
                      <div className="text-xs text-dark-500 mb-1">ACOS</div>
                      <div className="text-base font-bold text-cyan-400">19.2%</div>
                      <div className="text-xs text-green-400">-1.5%</div>
                    </div>
                    <div className="p-3 rounded-lg bg-dark-800/50">
                      <div className="text-xs text-dark-500 mb-1">TACOS</div>
                      <div className="text-base font-bold text-white">11.8%</div>
                      <div className="text-xs text-green-400">-0.8%</div>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                    <div className="text-xs text-cyan-400 font-medium mb-1">AI Insight</div>
                    <div className="text-sm text-dark-300">
                      Top 3 keywords drove 42% of sales. Consider increasing bids on "kitchen organizer" variants.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div 
            className={`grid md:grid-cols-2 gap-6 transition-all duration-1000 delay-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {credibilityIndicators.map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 p-4 rounded-xl bg-dark-900/30 border border-cyan-500/10"
              >
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="font-medium text-white">{item.label}</div>
                  <div className="text-sm text-dark-500">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
